import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function splitLetters(el) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
        if (node.textContent.trim().length) textNodes.push(node);
    }

    textNodes.forEach(textNode => {
        const parts = textNode.textContent.split(/(\s+)/);
        const fragment = document.createDocumentFragment();

        parts.forEach(part => {
            if (/^\s+$/.test(part)) {
                fragment.appendChild(document.createTextNode(part));
            } else {
                const wordSpan = document.createElement('span');
                wordSpan.className = 'scrollytelling__word';
                for (const char of part) {
                    const letterSpan = document.createElement('span');
                    letterSpan.className = 'scrollytelling__letter';
                    letterSpan.textContent = char;
                    wordSpan.appendChild(letterSpan);
                }
                fragment.appendChild(wordSpan);
            }
        });

        textNode.parentNode.replaceChild(fragment, textNode);
    });

    return el.querySelectorAll('.scrollytelling__letter');
}

export function initScrollytelling() {
    if (prefersReducedMotion) return;

    const container = document.querySelector('.js-scrollytelling');
    if (!container) return;

    const sections = document.querySelectorAll('.js-scrollytelling .scrollytelling__section');

    ScrollTrigger.create({
        trigger: container,
        start: '33% center',
        end: '66% center',
        onEnter: () => container.classList.add('scrollytelling--sky-whisper'),
        onLeaveBack: () => container.classList.remove('scrollytelling--sky-whisper'),
        onLeave: () => container.classList.remove('scrollytelling--sky-whisper'),
        onEnterBack: () => container.classList.add('scrollytelling--sky-whisper'),
    });

    ScrollTrigger.create({
        trigger: container,
        start: '66% center',
        end: 'bottom bottom',
        onEnter: () => container.classList.add('scrollytelling--lemon'),
        onLeaveBack: () => container.classList.remove('scrollytelling--lemon'),
        onEnterBack: () => container.classList.add('scrollytelling--lemon'),
    });

    const parallaxSpeeds = { 1: 0, 2: -0.2, 3: -0.25, 4: 0, 5: -0.2 };
    const parallaxItems = [];

    sections.forEach(section => {
        const headline = section.querySelector('.scrollytelling__headline');
        if (!headline) return;

        const letters = splitLetters(headline);
        const cta = section.querySelector('.scrollytelling__cta');

        gsap.fromTo(letters,
            { opacity: 0 },
            {
                opacity: 1,
                stagger: 0.04,
                ease: 'none',
                scrollTrigger: {
                    trigger: headline,
                    start: 'top 80%',
                    end: 'bottom 80%',
                    scrub: 0.8,
                }
            }
        );

        if (cta) {
            gsap.fromTo(cta,
                { opacity: 0, y: 10 },
                {
                    opacity: 1,
                    y: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: headline,
                        start: 'top 60%',
                        end: 'bottom 80%',
                        scrub: 0.8,
                    }
                }
            );
        }

        section.querySelectorAll('.scrollytelling__imageItem').forEach(item => {
            const isBig = item.classList.contains('scrollytelling__imageItem--big');

            if (!isBig) {
                const match = item.className.match(/scrollytelling__imageItem--(\d+)/);
                const speed = match ? (parallaxSpeeds[match[1]] ?? 0) : 0;
                const baseTransform = getComputedStyle(item).transform;
                const sectionTop = section.getBoundingClientRect().top + window.scrollY;
                if (speed !== 0) parallaxItems.push({ item, speed, baseTransform, sectionTop });
            } else {
                gsap.fromTo(item,
                    { left: '40%', xPercent: -50, opacity: 0 },
                    {
                        left: '50%',
                        xPercent: -50,
                        opacity: 1,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: item,
                            start: 'top bottom',
                            end: 'bottom bottom',
                            scrub: true,
                        }
                    }
                );
            }
        });
    });

    if (parallaxItems.length) {
        const onScroll = () => {
            const factor = window.innerWidth < 768 ? 0.7 : 1.0;
            const scrollY = window.scrollY;
            parallaxItems.forEach(({ item, speed, baseTransform, sectionTop }) => {
                const base = baseTransform !== 'none' ? `${baseTransform} ` : '';
                item.style.transform = `${base}translateY(${(scrollY - sectionTop) * speed * factor}px)`;
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
    }
}